import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { useSite } from '../context/SiteContext';
import { pathFor } from '../i18n/routes';
import useSEO from '../hooks/useSEO';
import { IMG, VIDEO, MEDIA_SERVICE_IMAGES } from '../data/media';
import { MaskedLines, Kicker, Reveal } from '../components/Reveal';
import HoverRevealList from '../components/HoverRevealList';
import { CTABanner } from '../components/WorkGrid';

export default function Media() {
  const { lang, t } = useSite();
  useSEO(t.meta.media, 'media', lang);

  const services = t.media.services.map((s, i) => ({
    ...s,
    img: MEDIA_SERVICE_IMAGES[i],
  }));

  return (
    <div data-testid="media-page">
      <section className="relative flex min-h-[92svh] items-end overflow-hidden">
        <video
          autoPlay
          muted
          loop
          playsInline
          poster={IMG.video1}
          className="absolute inset-0 h-full w-full object-cover opacity-40"
          data-testid="media-hero-video"
        >
          <source src={VIDEO.cinema} type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-black/60" />
        <div className="absolute -right-32 top-1/4 h-[420px] w-[420px] rounded-full bg-fl-green/15 blur-[130px]" aria-hidden="true" />
        <div className="relative z-10 mx-auto w-full max-w-[1600px] px-5 pb-20 pt-40 sm:px-8">
          <Kicker className="!text-fl-green">F&L Media</Kicker>
          <h1 className="mt-6 max-w-4xl font-heading text-5xl font-black uppercase leading-[0.95] tracking-tight sm:text-7xl">
            <MaskedLines delay={0.2} lines={[t.media.title]} />
          </h1>
          <Reveal delay={0.5}>
            <p className="mt-6 max-w-xl text-base text-zinc-400 sm:text-lg">{t.media.desc}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-24 sm:py-32" data-testid="media-services">
        <div className="mx-auto mb-14 max-w-[1600px] px-5 sm:px-8">
          <Reveal>
            <Kicker>{t.media.servicesKicker}</Kicker>
            <h2 className="mt-4 font-heading text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
              {t.media.servicesTitle}
            </h2>
          </Reveal>
        </div>
        <HoverRevealList items={services} testIdPrefix="media-service" />
      </section>

      <section className="border-t border-zinc-900 py-24 sm:py-32" data-testid="media-work">
        <div className="mx-auto max-w-[1600px] px-5 sm:px-8">
          <Reveal className="flex flex-wrap items-end justify-between gap-6">
            <div>
              <Kicker>{t.media.workKicker}</Kicker>
              <h2 className="mt-4 font-heading text-4xl font-extrabold uppercase tracking-tight sm:text-5xl">
                {t.media.workTitle}
              </h2>
            </div>
            <Link
              to={pathFor('portfolio', lang)}
              data-testid="media-work-cta"
              data-cursor={t.cursor.view}
              className="group inline-flex items-center gap-2 border border-zinc-800 px-6 py-3 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:border-[#14DA64] hover:bg-[#14DA64] hover:text-black"
            >
              {t.media.workCta}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
            </Link>
          </Reveal>
        </div>
      </section>

      <CTABanner
        title={t.media.ctaTitle}
        text={t.media.ctaText}
        cta={t.media.ctaButton}
        email="media@flgroup.pt"
        to={pathFor('contact', lang)}
        testId="media-cta"
      />
    </div>
  );
}
